// Stripe Plan types (from Stripe API)
export interface StripePrice {
  id: string;
  currency: string;
  unitAmount: number;
  recurring: {
    interval: 'month' | 'year';
    intervalCount: number;
  } | null;
  type: 'one_time' | 'recurring';
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  description: string;
  metadata: Record<string, string>;
  marketingFeatures: string[];
  prices: StripePrice[];
}

// Subscription types
export type BillingCycle = 'monthly' | 'annual';

export type SubscriptionStatus =
  | 'active'
  | 'trialing'
  | 'past_due'
  | 'canceled'
  | 'incomplete'
  | 'incomplete_expired'
  | 'unpaid';

export type PlanName = 'free' | 'basic' | 'pro' | 'enterprise';

// Supported currencies
export type Currency = 'usd' | 'eur' | 'brl' | 'pln';

// API Plan type (from backend /api/v1/subscription_plans)
export interface ApiPlan {
  id: number;
  name: string;
  code: PlanName;
  description: string;
  monthly_price: number;
  yearly_price: number;
  monthly_price_from_yearly?: number;
  yearly_discount_percentage?: number;
  trial_days: number;
  has_trial: boolean;
  currency?: Currency;
  limits: {
    max_users: number;
    max_bookings_per_month: number;
    max_services: number;
    max_providers: number;
    storage_gb: number;
    api_requests_per_month: number;
  };
  features: Record<string, boolean>;
  position: number;
}

// API response with metadata
export interface ApiPlansResponse {
  success: boolean;
  data: ApiPlan[];
  meta: {
    currency: Currency;
    country: string;
  };
}

// Helper to convert API plan to frontend Plan type
export function apiPlanToFrontendPlan(apiPlan: ApiPlan): Plan {
  const isUnlimited = (value: number) => value >= 9999;

  // Build additional features list based on enabled features
  const additionalFeatures: string[] = [];
  if (apiPlan.features.email_notifications) additionalFeatures.push('Email notifications');
  if (apiPlan.features.sms_notifications) additionalFeatures.push('SMS notifications');
  if (apiPlan.features.calendar_integration) additionalFeatures.push('Calendar integrations');
  if (apiPlan.features.team_collaboration) additionalFeatures.push('Team collaboration');
  if (apiPlan.features.advanced_analytics) additionalFeatures.push('Advanced analytics');
  if (apiPlan.features.api_access) additionalFeatures.push('API access');
  if (apiPlan.features.webhooks) additionalFeatures.push('Webhooks');
  if (apiPlan.features.custom_domain) additionalFeatures.push('Custom domain');
  if (apiPlan.features.priority_support) additionalFeatures.push('Priority support');
  if (apiPlan.features.white_label) additionalFeatures.push('White label');
  if (apiPlan.features.sla) additionalFeatures.push('SLA guarantee');
  if (apiPlan.trial_days > 0) additionalFeatures.push(`${apiPlan.trial_days}-day free trial`);

  return {
    id: apiPlan.code,
    name: apiPlan.code,
    displayName: apiPlan.name.replace(' Plan', ''),
    description: apiPlan.description,
    price: {
      monthly: apiPlan.monthly_price,
      annual: apiPlan.yearly_price,
    },
    features: {
      providers: isUnlimited(apiPlan.limits.max_providers) ? 'unlimited' : apiPlan.limits.max_providers,
      services: isUnlimited(apiPlan.limits.max_services) ? 'unlimited' : apiPlan.limits.max_services,
      bookings: isUnlimited(apiPlan.limits.max_bookings_per_month) ? 'unlimited' : apiPlan.limits.max_bookings_per_month,
      additionalFeatures,
    },
    trialDays: apiPlan.trial_days,
    popular: apiPlan.code === 'pro',
  };
}

export interface Plan {
  id: string;
  name: PlanName;
  displayName: string;
  description: string;
  price: {
    monthly: number;
    annual: number;
  };
  features: {
    providers: number | 'unlimited';
    services: number | 'unlimited';
    bookings: number | 'unlimited';
    additionalFeatures: string[];
  };
  trialDays: number;
  popular?: boolean;
}

export interface Subscription {
  id: number;
  organizationId: number;
  planId: string;
  planCode?: string;
  planName: PlanName;
  status: SubscriptionStatus;
  billingCycle: BillingCycle;
  currency?: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  trialStart?: string;
  trialEnd?: string;
  cancelAt?: string;
  canceledAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSubscriptionRequest {
  planId: string;
  billingCycle: BillingCycle;
  paymentMethodId: string;
  promotionCode?: string;
}

// Promo code types
export interface PromoCodeDiscount {
  type: 'percent' | 'amount';
  percentOff: number | null;
  amountOff: number | null;
  currency: string | null;
  duration: 'once' | 'repeating' | 'forever';
  durationInMonths: number | null;
}

export interface PromoCodeRestrictions {
  firstTimeTransaction: boolean;
  minimumAmount: number | null;
  minimumAmountCurrency: string | null;
}

export interface PromoCodeValidationResponse {
  valid: boolean;
  promotionCodeId: string;
  code: string;
  discount: PromoCodeDiscount;
  restrictions: PromoCodeRestrictions;
}

export interface ChangeSubscriptionRequest {
  newPlanId: string;
  billingCycle?: BillingCycle;
}

export interface SubscriptionResponse {
  subscription: Subscription;
  nextBillingDate?: string;
  nextBillingAmount?: number;
  nextBillingCurrency?: string;
}

// Payment method types
export interface PaymentMethod {
  id: string;
  type: 'card';
  card: {
    brand: string;
    last4: string;
    expMonth: number;
    expYear: number;
  };
  isDefault: boolean;
  createdAt: string;
}

export interface AddPaymentMethodRequest {
  paymentMethodId: string;
}

// Invoice types
export interface Invoice {
  id: string;
  subscriptionId: number;
  amount: number;
  status: 'draft' | 'open' | 'paid' | 'void' | 'uncollectible';
  currency: string;
  periodStart: string;
  periodEnd: string;
  paidAt?: string;
  invoicePdf?: string;
  createdAt: string;
}

// Subscription limits and usage
export interface UsageMetric {
  current: number;
  limit: number;
  percentage: number;
  unlimited?: boolean;
}

export interface SubscriptionLimits {
  currentPlan: {
    code: string;
    name: string;
    displayName: string;
    description: string;
  };
  usage: {
    users: UsageMetric;
    providers: UsageMetric;
    services: UsageMetric;
    bookingsThisMonth: UsageMetric;
  };
  features: {
    emailNotifications: boolean;
    smsNotifications: boolean;
    dataExport: boolean;
    calendarIntegration: boolean;
    apiAccess: boolean;
    webhooks: boolean;
    customIntegrations: boolean;
    teamCollaboration: boolean;
    advancedAnalytics: boolean;
    customDomain: boolean;
    whiteLabel: boolean;
    prioritySupport: boolean;
    sla: boolean;
  };
  metadata: Record<string, any>;
}

export interface SubscriptionError {
  type: 'subscription_limit' | 'feature_disabled' | 'permission_denied';
  limit?: string;
  limitName?: string;
  current?: number;
  max?: number;
  feature?: string;
  featureName?: string;
  description?: string;
  currentPlan?: string;
  requiredPlan?: string;
  recommendedPlan?: string;
  message: string;
  upgradeUrl?: string;
}

// Available plans configuration
export const PLANS: Plan[] = [
  {
    id: 'free',
    name: 'free',
    displayName: 'Free',
    description: 'Perfect for getting started',
    price: {
      monthly: 0,
      annual: 0,
    },
    features: {
      providers: 1,
      services: 5,
      bookings: 50,
      additionalFeatures: [
        'Basic booking management',
        'Email notifications',
        'Customer portal',
      ],
    },
    trialDays: 0,
  },
  {
    id: 'basic',
    name: 'basic',
    displayName: 'Basic',
    description: 'Great for small teams',
    price: {
      monthly: 29,
      annual: 290,
    },
    features: {
      providers: 3,
      services: 20,
      bookings: 500,
      additionalFeatures: [
        'All Free features',
        'Calendar integrations',
        'SMS notifications',
        'Custom branding',
        '14-day free trial',
      ],
    },
    trialDays: 14,
  },
  {
    id: 'pro',
    name: 'pro',
    displayName: 'Pro',
    description: 'For growing businesses',
    price: {
      monthly: 79,
      annual: 790,
    },
    features: {
      providers: 10,
      services: Infinity,
      bookings: 2000,
      additionalFeatures: [
        'All Basic features',
        'Advanced analytics',
        'API access',
        'Priority support',
        'Custom workflows',
        '14-day free trial',
      ],
    },
    trialDays: 14,
    popular: true,
  },
  {
    id: 'enterprise',
    name: 'enterprise',
    displayName: 'Enterprise',
    description: 'For large organizations',
    price: {
      monthly: 199,
      annual: 1990,
    },
    features: {
      providers: Infinity,
      services: Infinity,
      bookings: Infinity,
      additionalFeatures: [
        'All Pro features',
        'Unlimited everything',
        'Dedicated support',
        'Custom integrations',
        'SLA guarantee',
        '30-day free trial',
      ],
    },
    trialDays: 30,
  },
];
